import {
  Popover as MuiPopover,
  styled,
  SxProps,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React from 'react'
import TestIds from '../../../testIds'
import { useSelector } from 'react-redux'
import {
  DigitalFarmerAccount,
  selectAllFarmerAccounts,
  selectCurrentFarmerAccount,
} from '../storage'
import Autocomplete from '../../../components/Autocomplete'
import Modal from '../../../components/Modal'
import AccountSwitcher from './AccountSwitcher'
import { APPBAR_HEIGHT, PAGE_PADDING_SIDE } from '../../../util/consts'

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
`

const Popover = styled(MuiPopover)({
  width: '400px',
  maxHeight: '679px',
  top: `calc(${APPBAR_HEIGHT.md - 24}px + var(--safe-area-statusbarHeight))`,
  left: `calc(100% - 400px - ${PAGE_PADDING_SIDE.md}px + 16px)`,
})

const getStyle = ({ color, alignment, labelStyle, disabled }: StyleProps) => {
  const textAlign = alignment === 'left' ? 'left' : 'right'
  return {
    background: 'transparent',
    border: 'none',

    '& label': {
      ...labelStyle,
      color: color,
      textAlign: alignment,
      '&.Mui-focused': {
        color: color,
      },
    },
    '& input': {
      color: color,
      textAlign: disabled ? textAlign : 'left',
      paddingLeft: '0px !important',
      paddingTop: '0px !important',
    },
    '& fieldset': {
      borderRadius: '0px',
      border: 'none',
      borderBottom: disabled ? `none !important` : `1px solid ${color} !important`,
    },
    '&:hover fieldset': {
      borderBottom: disabled ? `none !important` : `2px solid ${color} !important`,
    },

    '& .MuiInputAdornment-root': {
      color: color,
      display: disabled ? `none` : 'flex',
    },

    '& .MuiAutocomplete-endAdornment .MuiIconButton-root': {
      color: color,
      display: disabled ? `none` : 'flex',
    },

    '& .MuiFormHelperText-root': {
      display: 'none',
    },
    '& .MuiAutocomplete-inputRoot': {
      paddingRight: disabled ? '0px !important' : '',
      background: 'transparent',
      paddingLeft: 0,
      ...(disabled ? { paddingBottom: '0px !important' } : {}),
    },
  }
}

interface StyleProps {
  alignment?: 'left' | 'right'
  color?: string
  labelStyle?: SxProps<Theme>
  disabled?: boolean
}

interface Props {
  alignment?: 'left' | 'right'
  color?: string
  labelStyle?: SxProps<Theme>
  sx?: SxProps<Theme>
}

const AccountSelector = ({ sx, color, alignment, labelStyle }: Props) => {
  const theme = useTheme()
  const isMd = useMediaQuery(theme.breakpoints.down('md'))
  const selectedAccount = useSelector(selectCurrentFarmerAccount)
  const farmlandsAccounts = useSelector(selectAllFarmerAccounts)
  const [openModal, setOpenModal] = React.useState(false)
  const [openPopper, setOpenPopper] = React.useState(false)
  const options = React.useMemo(() => {
    const accounts: DigitalFarmerAccount[] = []
    if (farmlandsAccounts) {
      Object.keys(farmlandsAccounts.accounts).forEach((key) =>
        accounts.push(farmlandsAccounts.accounts[key]),
      )
    }
    return accounts
  }, [farmlandsAccounts])
  const disabled = options.length <= 1

  const renderInModal = React.useMemo(
    () => (
      <Modal
        title='Accounts'
        open={openModal}
        handleClose={() => setOpenModal(false)}
        fullScreen={isMd}
      >
        <AccountSwitcher closeAccountSwither={() => setOpenModal(false)} />
      </Modal>
    ),
    [openModal, setOpenModal],
  )

  const renderInPopper = React.useMemo(
    () => (
      <Popover
        open={openPopper}
        onClose={() => setOpenPopper(false)}
        anchorEl={null}
        slotProps={{
          paper: {
            sx: { width: '100%', p: 2, borderRadius: 0 },
          },
        }}
      >
        <AccountSwitcher closeAccountSwither={() => setOpenPopper(false)} />
      </Popover>
    ),
    [openPopper, setOpenPopper],
  )

  return (
    <>
      {selectedAccount && (
        <StyledContainer>
          <Autocomplete
            testId={TestIds.components.accountSwitch.selector}
            sx={{
              ...sx,
              ...getStyle({
                color: color,
                alignment: alignment,
                labelStyle: labelStyle,
                disabled,
              }),
            }}
            open={false}
            onOpen={() => (isMd ? setOpenModal(true) : setOpenPopper(true))}
            disabled={disabled}
            label={`${farmlandsAccounts?.contactFirstName} ${farmlandsAccounts?.contactLastName}, ${selectedAccount?.accountNumber}`}
            options={[]}
            value={selectedAccount?.accountName}
            disableClearable
          />
        </StyledContainer>
      )}
      {isMd ? renderInModal : renderInPopper}
    </>
  )
}

export default AccountSelector
